import { DeepKeyOf } from '@/components/_dashboard/promotion/formik/type';
import { Icon } from '@iconify/react';
import LoadingButton from '@mui/material/Button';
import { Box, IconButton, InputBase, Paper } from '@mui/material';
import { useCallback, useState } from 'react';
export interface IExtraAction {
    label?: string;
    onBtnClick?: (value?: string) => Promise<void>;
}

export interface ISearchBox<T> {
    list: DeepKeyOf<T>[];
    placeholder?: string;
    extraAction?: IExtraAction;
}

interface IICommonTableToolbarProps {
    placeholder?: string;
    handleSearchValueOnChange: (value: string) => void;
    extraAction?: IExtraAction;
}

export const ICommonTableToolbar = (props: IICommonTableToolbarProps): JSX.Element => {
    const { placeholder, handleSearchValueOnChange, extraAction } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    // handle search value change
    const handleSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchValue(event.target.value);
        handleSearchValueOnChange(event.target.value);
    };
    // clear button click
    const handleClearButtonClick = (): void => {
        setSearchValue('');
        handleSearchValueOnChange('');
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            // prevent form submit
            event.preventDefault();
        }
    };
    // extra action button click
    const handleExtraActionButtonClick = useCallback(
        async (value: string): Promise<void> => {
            if (extraAction?.onBtnClick) {
                try {
                    if (isLoading) return; // Prevent multiple clicks
                    setIsLoading(true);
                    await extraAction.onBtnClick(value);
                } catch (error) {
                    console.error('Error in extra action button click:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        },
        [extraAction]
    );
    return (
        <Box sx={{ display: 'flex' }}>
            <Paper
                component="form"
                sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 400,
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: isFocused ? 3 : 0,
                    transition: 'box-shadow 0.3s ease'
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                <IconButton sx={{ p: '10px' }} aria-label="menu">
                    <Icon icon="material-symbols-light:search" width="24" height="24" />
                </IconButton>
                <InputBase
                    sx={{
                        ml: 1,
                        flex: 1
                    }}
                    value={searchValue}
                    onChange={handleSearchValueChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || 'Search...'}
                    inputProps={{ 'aria-label': placeholder || '' }}
                />
                {searchValue && (
                    <IconButton onClick={handleClearButtonClick} type="button" sx={{ p: '10px' }} aria-label="search">
                        <Icon icon="ic:outline-clear" width="24" height="24" />
                    </IconButton>
                )}
            </Paper>
            {extraAction && (
                <LoadingButton
                    loading={isLoading}
                    onClick={async () => {
                        await handleExtraActionButtonClick(searchValue);
                    }}
                    sx={{ ml: 1 }}
                >
                    {extraAction.label || 'Search'}
                </LoadingButton>
            )}
        </Box>
    );
};
